import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { format } from 'date-fns'
import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { METHOD_TO_ICON_MAP } from 'components/common/chip/MethodChip'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import WeeklySummaryReadonly from 'components/customer-details/content/configured-services/actions/weekly-summary/WeeklySummaryReadonly'
import RouteChip from 'components/customer-details/content/work-orders/common/RouteChip'
import UnassignedChip from 'components/customer-details/content/work-orders/common/UnassignedChip'
import ServiceActionTypeHelper from 'components/services/ServiceActionTypeHelper'
import ServiceTypeChip from 'components/services/ServiceTypeChip'
import WorkOrderStatusChip from 'components/work-order/WorkOrderStatusChip'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import T from 'T'
import { createDateFromString } from 'utils/date'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { getPricingPeriod } from 'utils/service'

import type { SxProps, Theme } from '@mui/material/styles'

type WeekWorkOrder = {
  id?: string
  workOrderNumber?: string | null
  workOrderStatus?: string | null
  completionStatus?: string | null
  intentStatus?: string | null
  serviceActionName?: string | null
  eventActionType?: string | null
  serviceDate?: string | null
  servicedAt?: string | null
  servicedByFirstName?: string | null
  servicedByLastName?: string | null
  routeId?: string | null
  routeName?: string | null
  routeColor?: string | null
  sequence?: number | null
}

type MapService = {
  serviceName?: string
  servicePrice?: number | string
  pricingPeriodInterval?: string
  pricingPeriodPer?: string | number
  methodType?: string
  measureUnit?: string
  measureVolume?: number
  pricedServiceType?: string
  workOrders?: WeekWorkOrder[]
}

type Props = {
  service?: MapService | null
  weekWorkOrders?: WeekWorkOrder[] | null
  showList?: boolean
  sx?: SxProps<Theme>
}


function SchedulingMapServicePopover({
  service,
  weekWorkOrders,
  showList = false,
  sx = {
    width: 440,
    maxWidth: 480,
    p: 1,
  },
}: Props) {
  const theme = useTheme()
  const { watch } = useFormContext()
  const {
    serviceName,
    servicePrice = 0,
    pricingPeriodInterval = '',
    pricingPeriodPer,
    methodType = '',
    measureUnit = '',
    measureVolume = 0,
    pricedServiceType,
    workOrders = [],
  } = service || {}
  const pricingPeriodText = pricingPeriodInterval === 'Instance' ? '' : getPricingPeriod(pricingPeriodPer, pricingPeriodInterval)
  const measureLabel = `${measureVolume} ${getMeasureUnit(measureUnit)}`
  const MethodIcon = METHOD_TO_ICON_MAP[methodType.toUpperCase()] ?? null
  const list = useMemo(() => (Array.isArray(weekWorkOrders) ? weekWorkOrders : workOrders || []), [weekWorkOrders, workOrders])
  const selectedDays = watch ? watch('selectedDays') || {} : {}
  const filteredList = useMemo(() => {
    const base = Array.isArray(list) ? list : []
    const selected = Object.entries(selectedDays)
      .filter(([, v]) => !!v)
      .map(([k]) => k)
    if (selected.length === 0) return base
    return base.filter(wo => {
      const { serviceDate } = wo
      if (!serviceDate) return false
      try {
        const woDate = createDateFromString(serviceDate)
        if (!woDate) return false
        const dayName = format(woDate, 'EEEE')
        return selected.includes(dayName)
      } catch (e) {
        return false
      }
    })
  }, [list, selectedDays])

  return (
    <Box sx={sx}>
      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="nowrap">
        {MethodIcon && <MethodIcon width={20} height={20} color={theme.palette.text.secondary} style={{ minWidth: 20 }} />}
        <Typography variant="h5" fontWeight={600} noWrap title={serviceName}>
          {serviceName}
        </Typography>
        <Box ml="auto" flexWrap="nowrap" display="flex" alignItems="center" gap={0.5}>
          <Typography variant="h6" fontWeight={600} display="flex" alignItems="center" gap={0.5}>
            <HHDisplayMoney formatToDollars={false} value={servicePrice} />
          </Typography>
          {pricingPeriodText && <Typography variant="subtitle2" color="text.secondary">{`/ ${pricingPeriodText}`}</Typography>}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <ServiceTypeChip serviceType={pricedServiceType} />
        {/* {quantity != null && <ContainersChip variant="outlined" size="small" label={`Quantity | ${quantity}`} />} */}
        {measureUnit && <MeasureChip label={measureLabel} measureUnit={measureUnit} />}
        <Stack sx={{ mt: 0.5, width: '100%' }} justifyContent="center" alignItems="center">
          <WeeklySummaryReadonly serviceType={pricedServiceType} workOrders={list} />
        </Stack>
      </Box>

      {showList && (
        <>
          <Divider />
          <Box mt={1} display="flex" flexDirection="column" gap={1}>
            {filteredList.map((workOrder, index) => {
              const workOrderStatus = workOrder?.completionStatus || workOrder?.workOrderStatus
              const { serviceDate, servicedAt, routeId, routeName, routeColor } = workOrder
              const serviceActionName = workOrder.serviceActionName ?? ''
              const eventActionType = workOrder.eventActionType ?? ''
              const servicedByFirstName = workOrder.servicedByFirstName ?? ''
              const servicedByLastName = workOrder.servicedByLastName ?? ''
              const sequence = workOrder.sequence ?? 0
              const woDate = createDateFromString(serviceDate)
              const formattedDate = woDate ? format(woDate, 'MM/dd') : ''

              return (
                <>
                  <ListItem disableGutters disablePadding sx={{ gap: 8 }}>
                    <ListItemText
                      sx={{ m: 0 }}
                      primary={workOrder.workOrderNumber}
                      slotProps={{
                        primary: { variant: 'h6', fontWeight: 500 },
                      }}
                      secondary={formattedDate}
                    />
                    <ListItemIcon>
                      {!routeId && <UnassignedChip />}
                      {routeId && <RouteChip routeColor={routeColor} routeName={routeName} sequence={sequence} serviceDate={serviceDate} />}
                    </ListItemIcon>
                  </ListItem>
                  <ListItem
                    disableGutters
                    disablePadding
                    sx={{ gap: 8, justifyContent: 'space-between', pb: 0.5 }}
                    divider={index !== filteredList.length - 1}
                  >
                    <ListItemIcon>
                      <ServiceActionTypeHelper
                        serviceActionType={serviceActionName}
                        eventActionType={eventActionType}
                        serviceType={pricedServiceType}
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemIcon>
                      <WorkOrderStatusChip
                        intentStatus={workOrder.intentStatus ?? undefined}
                        servicedAt={servicedAt ?? undefined}
                        servicedBy={`${servicedByFirstName} ${servicedByLastName}`.trim()}
                        status={workOrderStatus ?? undefined}
                      />
                    </ListItemIcon>
                  </ListItem>
                </>
              )
            })}
            {filteredList.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {T.NO_WORK_ORDERS_FOR_WEEK}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

export default SchedulingMapServicePopover
